
import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { HeroVariantProps, getTextureClass, getOutlineStyle } from '../shared';
import { StoreImage } from '../../../ui/StoreImage';

export const HeroFade: React.FC<HeroVariantProps> = (props) => {
    const { theme, student, className, title, text, imageUrl, mobileImageUrl, onScrollToShop, parallaxRef, customButtons, customBadge, heroContentPosition = 50, heroContentPositionY = 50, heroTextAlignment = 'left', heroButtonColor, heroButtonTextColor } = props;
    const [mounted, setMounted] = useState(false);
    // Bilden tonas in när den laddats istället för att poppa upp över den
    // mörka ytan. Redan cachad bild (complete) visas direkt.
    const imgRef = useRef<HTMLImageElement>(null);
    const [imageReady, setImageReady] = useState(false);

    useEffect(() => { setMounted(true); }, []);
    useEffect(() => {
        const img = imgRef.current;
        setImageReady(!!img && img.complete && img.naturalWidth > 0);
    }, [imageUrl, mobileImageUrl]);

    const titleColor = props.titleColor || '#FFFFFF';
    const textColor = props.textColor || 'rgba(255,255,255,0.9)';
    const isDesktop = window.innerWidth > 768;

    const cssTopPercent = 100 - heroContentPositionY;
    const cssLeftPercent = isDesktop ? heroContentPosition : 50;

    const textureClass = getTextureClass(props.heroTitleTexture);
    
    const titleOutline = getOutlineStyle(props.heroTitleOutlineWidth, props.heroTitleOutlineColor || titleColor);
    const textOutline = getOutlineStyle(props.heroTextOutlineWidth, props.heroTextOutlineColor || textColor);

    const altText = title ? title.replace(/<[^>]*>?/gm, '') : "Kaffeförsäljning till klasskassan";

    return (
        <div className={`relative ${props.heightClass} flex overflow-hidden bg-stone-900`}>
            {/* Image */}
            <div 
                ref={parallaxRef}
                    data-parallax
                className="absolute inset-0 w-full h-[120%] -top-[10%] overflow-hidden"
                style={{ transform: 'translate3d(0,0,0)', willChange: 'transform' }}
            >
                <div className={`w-full h-full transition-transform duration-[15000ms] ease-out ${mounted ? 'scale-105' : 'scale-100'}`}>
                    <StoreImage
                        ref={imgRef}
                        src={imageUrl}
                        mobileSrc={mobileImageUrl || undefined}
                        width={1600}
                        alt={altText}
                        onLoad={() => setImageReady(true)}
                        className={`w-full h-full object-cover transition-opacity duration-700 ease-out ${imageReady ? 'opacity-100' : 'opacity-0'}`}
                    />
                </div>
            </div>

            {/* Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent md:bg-gradient-to-r md:from-black/80 md:via-black/30 md:to-transparent z-10 pointer-events-none"></div>

            {/* Content Wrapper */}
            <div className="absolute inset-0 px-[6%] py-[6%] pointer-events-none z-20">
                <div className="relative w-full h-full">
                    <div 
                        className={`
                            pointer-events-auto
                            transition-all duration-1000 ease-out max-w-2xl w-full
                            absolute
                            flex flex-col 
                            ${heroTextAlignment === 'center' ? 'items-center' : heroTextAlignment === 'right' ? 'items-end' : 'items-start'}
                            ${mounted ? 'opacity-100' : 'opacity-0 translate-y-6'}
                        `}
                        style={{
                            left: `${cssLeftPercent}%`,
                            top: `${cssTopPercent}%`,
                            transform: `translate(-${isDesktop ? cssLeftPercent : 50}%, -${cssTopPercent}%)`,
                            textAlign: heroTextAlignment
                        }}
                    >
                        {customBadge ? customBadge : (student && (
                            <div className="mb-5 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10 text-white text-[9px] font-bold uppercase tracking-widest">
                                <Star size={10} className="text-yellow-400 fill-yellow-400" /> {student.name} {className ? `& ${className}` : ''}
                            </div>
                        ))}

                        <h1 
                            className={`${props.titleSizeClass} font-heading font-black mb-4 tracking-tight leading-[1.1] ${props.titleShadowClass || 'drop-shadow-lg'} ${textureClass}`}
                            style={{ 
                                color: textureClass ? 'transparent' : titleColor, 
                                fontFamily: theme?.heroTitleFont,
                                filter: textureClass ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' : undefined,
                                ...titleOutline
                            }}
                        >
                            {title}
                        </h1>

                        <p 
                            className={`${props.subtitleSizeClass} font-body font-medium leading-relaxed mb-8 text-white/90 max-w-lg ${props.textShadowClass || 'drop-shadow'}`}
                            style={{ 
                                color: textColor, 
                                fontFamily: theme?.heroTextFont,
                                ...textOutline
                            }}
                        >
                            {text}
                        </p>

                        {customButtons}
                    </div>
                </div>
            </div>
        </div>
    );
};
